import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { formsApi, apiUtils } from '../services/formsApi';
import { computeFieldStepTitles } from '../utils/formSteps';
import { findField, findRoutingField } from '../utils/formLabels';

// ====== HOOKS PERSONALIZADOS ======

// Hook para gerenciar formulários
const useForms = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchForms = useCallback(async () => {
    try {
      console.log('🔄 FormManagement: Iniciando fetchForms...');
      setLoading(true);
      setError(null);
      
      const response = await formsApi.getForms();
      console.log('📊 FormManagement: Response recebido:', response);
      
      // Response é { success: true, data: [...] }
      const formsData = Array.isArray(response) ? response : (response?.data || response?.forms || []);
      console.log('📋 FormManagement: Forms processados:', formsData);
      setForms(formsData);
    } catch (error) {
      console.error('❌ FormManagement: Erro ao carregar formulários:', error);
      const errorInfo = apiUtils.handleError(error);
      setError(errorInfo.message);
      toast.error(errorInfo.message);
    } finally {
      setLoading(false);
      console.log('✅ FormManagement: Loading finalizado');
    }
  }, []);

  const toggleFormStatus = useCallback(async (formId, currentStatus) => {
    try {
      const response = await formsApi.toggleFormStatus(formId);
      
      // Atualizar o formulário na lista
      setForms(prevForms => 
        prevForms.map(form => 
          form.id === formId 
            ? { ...form, is_active: response.data.is_active }
            : form
        )
      );
      
      toast.success(response.message);
    } catch (error) {
      console.error('Erro ao alternar status:', error);
      const errorInfo = apiUtils.handleError(error);
      toast.error(errorInfo.message);
    }
  }, []);

  const deleteForm = useCallback(async (formId) => {
    try {
      await formsApi.deleteForm(formId);
      setForms(prevForms => prevForms.filter(form => form.id !== formId));
      toast.success('Formulário deletado com sucesso');
    } catch (error) {
      console.error('Erro ao deletar formulário:', error);
      const errorInfo = apiUtils.handleError(error);
      toast.error(errorInfo.message);
    }
  }, []);

  return {
    forms,
    loading,
    error,
    fetchForms,
    toggleFormStatus,
    deleteForm
  };
};

// Hook para gerenciar modal de formulário
const useFormModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_slug: '',
    client_name: '',
    company_logo: '',
    is_active: true,
    fields: [],
    create_client_admin: false,
    client_admin_data: {
      username: '',
      email: '',
      full_name: '',
      password: ''
    }
  });
  
  const [logoUploading, setLogoUploading] = useState(false);

  // Função para fazer upload do logo
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tamanho do arquivo (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Arquivo muito grande! Máximo 5MB.');
      return;
    }

    // Validar tipo do arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Tipo de arquivo não permitido! Use JPG, PNG, SVG, GIF ou WebP.');
      return;
    }

    setLogoUploading(true);

    try {
      // Converter arquivo para Base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const logoBase64 = e.target.result;
          
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app'}/api/forms/upload-logo`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              logoBase64: logoBase64,
              fileName: file.name
            })
          });

          const result = await response.json();

          if (result.success) {
            setFormData(prev => ({ ...prev, company_logo: result.logoUrl }));
            alert('Logo enviado com sucesso!');
          } else {
            throw new Error(result.error || 'Erro ao enviar logo');
          }
        } catch (error) {
          console.error('Erro no upload:', error);
          alert('Erro ao enviar logo: ' + error.message);
        } finally {
          setLogoUploading(false);
          // Limpar o input
          event.target.value = '';
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      alert('Erro ao processar arquivo: ' + error.message);
      setLogoUploading(false);
      event.target.value = '';
    }
  };

  const openCreateModal = useCallback(() => {
    setEditingForm(null);
    setFormData({
      title: '',
      description: '',
      client_slug: '',
      client_name: '',
      company_logo: '',
      is_active: true,
      fields: [],
      create_client_admin: false,
      client_admin_data: {
        username: '',
        email: '',
        full_name: '',
        password: ''
      }
    });
    setIsOpen(true);
  }, []);

  const openEditModal = useCallback(async (form) => {
    try {
      const response = await formsApi.getForm(form.id);
      const formData = response.data;
      // Garantir que fields é sempre array
      let safeFields = formData.fields;
      if (typeof safeFields === 'string') {
        try {
          safeFields = JSON.parse(safeFields);
        } catch (e) {
          safeFields = [];
        }
      } else if (!Array.isArray(safeFields)) {
        safeFields = [];
      }
      // fields já parseado: o salvamento compara com ele (sobreQuemImpact).
      setEditingForm({ ...formData, fields: safeFields });
      setFormData({
        title: formData.title,
        description: formData.description,
        client_slug: formData.client_slug,
        client_name: formData.client_name,
        company_logo: formData.company_logo || '',
        is_active: formData.is_active,
        fields: safeFields,
        create_client_admin: false,
        client_admin_data: {
          username: '',
          email: '',
          full_name: '',
          password: ''
        }
      });
      setIsOpen(true);
    } catch (error) {
      console.error('Erro ao carregar formulário:', error);
      const errorInfo = apiUtils.handleError(error);
      toast.error(errorInfo.message);
    }
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setEditingForm(null);
  }, []);

  return {
    isOpen,
    editingForm,
    formData,
    setFormData,
    openCreateModal,
    openEditModal,
    closeModal,
    handleLogoUpload,
    logoUploading
  };
};

// ====== COMPONENTES AUXILIARES ======

/**
 * Converte o rótulo da pergunta em um "Nome do Campo" válido (a chave sob a
 * qual a resposta é gravada na denúncia): sem acento, minúsculo, separado por
 * underscore. Serve para sugerir o nome e evitar campos sem nome, cujas
 * respostas eram descartadas silenciosamente.
 */
const slugifyFieldName = (label) => {
  if (!label) return '';
  return String(label)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove acentos
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .split('_').filter(Boolean).slice(0, 5).join('_'); // nome curto e legível
};

// Campos obrigatórios para analytics que não podem ser removidos.
// sobre_quem NÃO está aqui: ele só serve a clientes que querem dividir a
// visualização por público (ver sobreQuemImpact) e é opcional.
const requiredAnalyticsFields = [
  'local_ocorrencia',
  'tipo_denuncia',
  'quando_ocorreu',
  'departamento',
  'relato_detalhado',
  'tentou_esconder',
  'presenciou_fatos',
  'arquivo'  // Campo de upload de arquivo obrigatório
];

/** Campo que divide a visualização e suas categorias ({value,label}), ou null se não há divisão. */
const sobreQuemCategorias = (fields) => {
  const field = findRoutingField(fields);
  if (!field) return null;
  const categorias = (field.options || [])
    .map((o) => (o && typeof o === 'object' ? o : { value: o, label: o }))
    .map((o) => ({ value: String(o.value ?? '').trim(), label: String(o.label || o.value || '').trim() }))
    .filter((c) => c.value);
  return { name: field.name, pergunta: field.label || field.name, categorias };
};

/**
 * O que muda no roteamento por público entre o formulário salvo e o editado.
 * O valor de sobre_quem decide quais client_admins veem a denúncia e recebem o
 * e-mail (middleware/adminLevels), e as categorias viram os "Níveis de Acesso"
 * de Gerenciar Usuários. Incluir, tirar ou mudar categorias altera esse fluxo
 * sem nenhum aviso na tela — por isso o salvamento pede confirmação.
 * Compara pelo valor gravado na denúncia; devolve os rótulos, para exibir.
 * Retorna null quando nada muda.
 */
const sobreQuemImpact = (before, after) => {
  const antes = sobreQuemCategorias(before);
  const depois = sobreQuemCategorias(after);
  if (!antes && !depois) return null;
  const rotulos = (lista) => lista.map((c) => c.label);
  if (!antes) return { tipo: 'incluido', pergunta: depois.pergunta, categorias: rotulos(depois.categorias) };
  if (!depois) return { tipo: 'removido', pergunta: antes.pergunta, categorias: rotulos(antes.categorias) };
  if (antes.name !== depois.name) {
    return { tipo: 'trocado', de: antes.pergunta, pergunta: depois.pergunta, categorias: rotulos(depois.categorias) };
  }
  const fora = (lista, outra) => lista.filter((c) => !outra.some((o) => o.value === c.value));
  const removidas = rotulos(fora(antes.categorias, depois.categorias));
  const novas = rotulos(fora(depois.categorias, antes.categorias));
  if (removidas.length === 0 && novas.length === 0) return null;
  return { tipo: 'categorias', pergunta: depois.pergunta, removidas, novas };
};

// Componente para campo de formulário
const FormField = ({ field, index, stepTitle, isRouting, onToggleRouting, onUpdate, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const isRequiredForAnalytics = requiredAnalyticsFields.includes(field.name);
  const canRemove = !isRequiredForAnalytics;

  const updateField = (key, value) => {
    onUpdate(index, { ...field, [key]: value });
  };

  const addOption = () => {
    const newOptions = [...(field.options || []), ''];
    updateField('options', newOptions);
  };

  const updateOption = (optionIndex, value) => {
    const newOptions = [...(field.options || [])];
    newOptions[optionIndex] = value;
    updateField('options', newOptions);
  };

  const removeOption = (optionIndex) => {
    const newOptions = (field.options || []).filter((_, i) => i !== optionIndex);
    updateField('options', newOptions);
  };

  return (
    <div className={`bg-white p-4 rounded-lg border mb-4 ${
      isRequiredForAnalytics 
        ? 'border-orange-300 bg-orange-50' 
        : 'border-gray-200'
    }`}>
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-lg font-semibold">Campo {index + 1}</h4>
          {/* Em que etapa do formulário público esta pergunta vai aparecer.
              Calculado pela MESMA regra que o formulário usa (utils/formSteps),
              e depende da POSIÇÃO do campo — por isso muda ao usar ↑ ↓. */}
          {stepTitle && (
            <span
              className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full"
              title="Etapa do formulário público em que esta pergunta vai aparecer. Use ↑ ↓ para mudar."
            >
              📍 {stepTitle}
            </span>
          )}
          {isRequiredForAnalytics && (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
              🔒 Analytics
            </span>
          )}
          {isRouting && (
            <span
              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
              title="Define quais admins do cliente veem cada denúncia e recebem o e-mail"
            >
              🔀 Divide a visualização
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {!isFirst && (
            <button
              type="button"
              onClick={() => onMoveUp(index)}
              className="px-2 py-1 bg-blue-500 text-white rounded text-sm"
            >
              ↑
            </button>
          )}
          {!isLast && (
            <button
              type="button"
              onClick={() => onMoveDown(index)}
              className="px-2 py-1 bg-blue-500 text-white rounded text-sm"
            >
              ↓
            </button>
          )}
          {canRemove ? (
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="px-2 py-1 bg-red-500 text-white rounded text-sm"
            >
              Remover
            </button>
          ) : (
            <button
              type="button"
              disabled
              className="px-2 py-1 bg-gray-400 text-white rounded text-sm cursor-not-allowed"
              title="Campo obrigatório para analytics - não pode ser removido"
            >
              🔒 Protegido
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Tipo de Campo</label>
          <select
            value={field.type || 'text'}
            onChange={(e) => updateField('type', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded"
          >
            <option value="text">Texto</option>
            <option value="email">Email</option>
            <option value="textarea">Área de Texto</option>
            <option value="radio">Múltipla Escolha</option>
            <option value="checkbox">Caixas de Seleção</option>
            <option value="select">Lista Suspensa</option>
            <option value="file">Upload de Arquivo</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Nome do Campo <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={field.name || ''}
            onChange={(e) => updateField('name', e.target.value)}
            className={`w-full p-2 border rounded ${
              !field.name || String(field.name).trim() === ''
                ? 'border-red-400 bg-red-50'
                : 'border-gray-300'
            }`}
            placeholder="nome_do_campo"
          />
          {(!field.name || String(field.name).trim() === '') && (
            <p className="mt-1 text-xs text-red-600">
              Obrigatório: é a chave onde a resposta é gravada. Sem ela, a resposta é perdida.
              Preencha o rótulo e este campo é sugerido automaticamente.
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Rótulo</label>
          <input
            type="text"
            value={field.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            // Se o nome ainda está vazio, sugere um a partir do rótulo ao sair do
            // campo — evita na origem o campo sem nome, que descarta a resposta.
            onBlur={(e) => {
              if (!field.name || String(field.name).trim() === '') {
                const sugerido = slugifyFieldName(e.target.value);
                if (sugerido) updateField('name', sugerido);
              }
            }}
            className="w-full p-2 border border-gray-300 rounded"
            placeholder="Pergunta ou rótulo do campo"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Placeholder</label>
          <input
            type="text"
            value={field.placeholder || ''}
            onChange={(e) => updateField('placeholder', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded"
            placeholder="Texto de exemplo"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            checked={field.required || false}
            onChange={(e) => updateField('required', e.target.checked)}
            disabled={isRequiredForAnalytics}
            className="mr-2"
          />
          <label className={`text-sm font-medium ${isRequiredForAnalytics ? 'text-gray-500' : ''}`}>
            Campo obrigatório
            {isRequiredForAnalytics && <span className="text-orange-600 ml-1">(sempre obrigatório para analytics)</span>}
          </label>
        </div>

        {/* Só pergunta de escolha única divide: o valor escolhido vira o Nível de Acesso */}
        {(field.type === 'radio' || field.type === 'select') && (
          <label
            className="flex items-center text-sm font-medium cursor-pointer"
            title="As opções desta pergunta viram os Níveis de Acesso em Gerenciar Usuários: cada admin do cliente só vê e só é avisado das denúncias das opções dele"
          >
            <input
              type="checkbox"
              checked={Boolean(isRouting)}
              onChange={(e) => onToggleRouting(index, e.target.checked)}
              className="mr-2"
            />
            🔀 Divide a visualização entre os admins
          </label>
        )}
      </div>

      {/* Opções para campos de múltipla escolha */}
        {(field.type === 'radio' || field.type === 'checkbox' || field.type === 'select') && (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">Opções</label>
            {(field.options || []).map((option, optionIndex) => {
              // Detectar se é objeto com value/label ou string simples
              const isObjectOption = typeof option === 'object' && option !== null;
              const optionValue = isObjectOption ? option.value : option;
              const optionLabel = isObjectOption ? option.label : option;
              const isSobreQuemField = field.name === 'sobre_quem' || isRouting;

              // Campo sobre_quem: valor interno e texto exibido são sempre o mesmo
              // texto (uma única entrada), já que esse valor também é usado como
              // resposta salva na denúncia e como "Nível de Acesso" em Gerenciar
              // Usuários — não há mais distinção entre "código interno" e "rótulo".
              if (isSobreQuemField) {
                return (
                  <div key={optionIndex} className="flex gap-2 mb-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">Categoria</label>
                      <input
                        type="text"
                        value={optionLabel}
                        onChange={(e) => {
                          const newOptions = [...(field.options || [])];
                          newOptions[optionIndex] = { value: e.target.value, label: e.target.value };
                          updateField('options', newOptions);
                        }}
                        className="w-full p-2 border border-gray-300 rounded text-sm"
                        placeholder="Ex: Diretoria Executiva"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => removeOption(optionIndex)}
                        className="px-3 py-2 bg-red-500 text-white rounded"
                        title="Remover opção"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={optionIndex} className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Valor interno (fixo)</label>
                    <input
                      type="text"
                      value={optionValue}
                      onChange={(e) => {
                        const newOptions = [...(field.options || [])];
                        if (isObjectOption) {
                          newOptions[optionIndex] = { ...option, value: e.target.value };
                        } else {
                          newOptions[optionIndex] = e.target.value;
                        }
                        updateField('options', newOptions);
                      }}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                      placeholder="Valor interno (ex: diretores)"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Texto exibido (editável)</label>
                    <input
                      type="text"
                      value={optionLabel}
                      onChange={(e) => {
                        const newOptions = [...(field.options || [])];
                        if (isObjectOption) {
                          newOptions[optionIndex] = { ...option, label: e.target.value };
                        } else {
                          newOptions[optionIndex] = e.target.value;
                        }
                        updateField('options', newOptions);
                      }}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                      placeholder="Texto exibido (ex: Diretoria Executiva)"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => removeOption(optionIndex)}
                      className="px-3 py-2 bg-red-500 text-white rounded"
                      title="Remover opção"
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => {
                const newOptions = [...(field.options || []), { value: '', label: '' }];
                updateField('options', newOptions);
              }}
              className="px-3 py-2 bg-green-500 text-white rounded text-sm"
            >
              + Adicionar Opção
            </button>
            {isRouting && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                <strong>ℹ️ Sobre este campo:</strong> esta pergunta divide a visualização deste cliente. As
                opções aparecem como "Nível de Acesso" em Gerenciar Usuários, para restringir quais admins da
                empresa veem as denúncias de cada opção e recebem o e-mail delas. Sem divisão, todos os admins
                do cliente veem e são avisados de tudo. Ao renomear ou remover uma opção aqui, revise as
                permissões dos admins em Gerenciar Usuários.
              </div>
            )}
          </div>
        )}

      {/* Condicionais */}
      <div className="mt-4">
        <label className="block text-sm font-medium mb-2">Campo Condicional</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Depende do campo</label>
            <input
              type="text"
              value={field.conditional?.field || ''}
              onChange={(e) => updateField('conditional', { 
                ...field.conditional, 
                field: e.target.value 
              })}
              className="w-full p-2 border border-gray-300 rounded text-sm"
              placeholder="nome_do_campo"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Quando o valor for</label>
            <input
              type="text"
              value={field.conditional?.value || ''}
              onChange={(e) => updateField('conditional', { 
                ...field.conditional, 
                value: e.target.value 
              })}
              className="w-full p-2 border border-gray-300 rounded text-sm"
              placeholder="valor_esperado"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ====== COMPONENTE PRINCIPAL ======

const FormManagement = () => {
  const {
    forms,
    loading,
    error,
    fetchForms,
    toggleFormStatus,
    deleteForm
  } = useForms();

  const {
    isOpen,
    editingForm,
    formData,
    setFormData,
    openCreateModal,
    openEditModal,
    closeModal,
    handleLogoUpload,
    logoUploading
  } = useFormModal();

  // Mudança no roteamento por público aguardando confirmação (ver sobreQuemImpact)
  const [impactoSobreQuem, setImpactoSobreQuem] = useState(null);

  // Carregar formulários ao montar componente
  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  // Controlar overflow do body quando modal estiver aberto
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  // Templates pré-definidos
  const templates = {
    vulkan: {
      title: 'CANAL DE DENÚNCIAS - Colaborador VULKAN',
      description: 'Canal oficial para denúncias corporativas da VULKAN',
      fields: [
        {
          id: 1,
          type: 'radio',
          label: 'A denúncia é sobre:',
          name: 'sobre_quem',
          required: true,
          options: [
            { value: 'diretores', label: 'Diretores ou Vice-Presidência' },
            { value: 'presidencia', label: 'Presidência' },
            { value: 'demais_colaboradores', label: 'Demais Colaboradores' }
          ]
        },
        {
          id: 2,
          type: 'radio',
          label: 'Você gostaria de se identificar?',
          name: 'identificacao',
          required: true,
          options: ['Sim', 'Não']
        },
        {
          id: 3,
          type: 'text',
          label: 'Nome completo',
          name: 'nome_completo',
          required: false,
          placeholder: 'Insira sua resposta',
          conditional: { field: 'identificacao', value: 'Sim' }
        },
        {
          id: 4,
          type: 'email',
          label: 'E-mail',
          name: 'email',
          required: false,
          placeholder: 'Insira sua resposta',
          conditional: { field: 'identificacao', value: 'Sim' }
        },
        {
          id: 5,
          type: 'radio',
          label: 'A denúncia se refere a algo ocorrido em...',
          name: 'local_ocorrencia',
          required: true,
          options: ['Planta Itatiba', 'Outra']
        },
        {
          id: 6,
          type: 'radio',
          label: 'O que você está denunciando?',
          name: 'tipo_denuncia',
          required: true,
          options: [
            'Assédio moral',
            'Assédio sexual',
            'Corrupção ou suborno',
            'Descumprimento de norma corporativa',
            'Desrespeito ou abuso de poder',
            'Discriminação',
            'Disparidade ou irregularidade nas relações trabalhistas',
            'Fraude',
            'Uso indevido de informações corporativas',
            'Outra'
          ]
        },
        {
          id: 7,
          type: 'radio',
          label: 'Quando ocorreu?',
          name: 'quando_ocorreu',
          required: true,
          options: [
            'Esta semana',
            'Este mês',
            'Neste semestre',
            'Neste ano',
            'Há mais de um ano',
            'Está ocorrendo',
            'Outra'
          ]
        },
        {
          id: 8,
          type: 'radio',
          label: 'Em que departamento ocorreu o fato?',
          name: 'departamento',
          required: true,
          options: [
            'Financeiro',
            'Compras',
            'Desenvolvimento (Transf. Tecnologia)',
            'Engenharia',
            'Marketing',
            'Operações: Usinagem ou Montagem',
            'Operações: Outros',
            'Vendas VDT',
            'Vendas VC / VL',
            'Recursos Humanos',
            'Tecnologia da Informação',
            'Outra'
          ]
        },
        {
          id: 9,
          type: 'textarea',
          label: 'Relate o ocorrido da forma mais detalhada possível',
          name: 'relato_detalhado',
          required: true,
          placeholder: 'Descreva os fatos detalhadamente...'
        },
        {
          id: 10,
          type: 'radio',
          label: 'Alguém tentou esconder o fato?',
          name: 'tentou_esconder',
          required: true,
          options: ['Sim', 'Não']
        },
        {
          id: 11,
          type: 'radio',
          label: 'Você presenciou os fatos?',
          name: 'presenciou_fatos',
          required: true,
          options: ['Sim', 'Não', 'Soube por meio de outra pessoa']
        },
        {
          id: 12,
          type: 'file',
          label: 'Anexar evidências (opcional)',
          name: 'arquivo',
          required: false,  // Opcional para o usuário
          placeholder: 'Selecione arquivos para anexar'
        }
      ]
    },
    standard: {
      title: 'CANAL DE DENÚNCIAS - Padrão',
      description: 'Canal oficial para denúncias corporativas',
      fields: [
        {
          id: 1,
          type: 'radio',
          label: 'Você gostaria de se identificar?',
          name: 'identificacao',
          required: true,
          options: ['Sim', 'Não']
        },
        {
          id: 2,
          type: 'text',
          label: 'Nome completo',
          name: 'nome_completo',
          required: false,
          placeholder: 'Insira sua resposta',
          conditional: { field: 'identificacao', value: 'Sim' }
        },
        {
          id: 3,
          type: 'email',
          label: 'E-mail',
          name: 'email',
          required: false,
          placeholder: 'Insira sua resposta',
          conditional: { field: 'identificacao', value: 'Sim' }
        },
        {
          id: 4,
          type: 'textarea',
          label: 'Relate o ocorrido da forma mais detalhada possível',
          name: 'relato_detalhado',
          required: true,
          placeholder: 'Descreva os fatos detalhadamente...'
        },
        {
          id: 5,
          type: 'file',
          label: 'Anexar evidências (opcional)',
          name: 'arquivo',
          required: false,  // Opcional para o usuário
          placeholder: 'Selecione arquivos para anexar'
        }
      ]
    }
  };

  // Funções para manipular campos
  const addField = () => {
    const newField = {
        id: Date.now() + Math.random(),
        type: 'text',
        label: '',
        name: '',
        required: false,
      placeholder: ''
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const updateField = (index, updatedField) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === index ? updatedField : field
      )
    }));
  };

  // Só um campo divide a visualização. Desmarcar deixa o cliente sem divisão —
  // o sobre_quem também é desmarcado, senão voltaria a dividir sozinho.
  const toggleRoutingField = (index, ligar) => {
    setFormData(prev => {
      const atual = findRoutingField(prev.fields);
      const sobreQuem = findField(prev.fields, 'sobre_quem');
      return {
        ...prev,
        fields: prev.fields.map((field, i) => {
          if (i === index) return { ...field, divide_visualizacao: ligar };
          if (field === atual || (!ligar && field === sobreQuem)) return { ...field, divide_visualizacao: false };
          return field;
        })
      };
    });
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const moveFieldUp = (index) => {
    if (index === 0) return;
    setFormData(prev => {
      const newFields = [...prev.fields];
      [newFields[index], newFields[index - 1]] = [newFields[index - 1], newFields[index]];
      return { ...prev, fields: newFields };
    });
  };

  const moveFieldDown = (index) => {
    setFormData(prev => {
      if (index === prev.fields.length - 1) return prev;
      const newFields = [...prev.fields];
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
      return { ...prev, fields: newFields };
    });
  };

  // Carregar template
  const loadTemplate = (templateKey) => {
    const template = templates[templateKey];
    if (template) {
      // Garantir que campos obrigatórios para analytics estejam sempre presentes
      let finalFields = [...template.fields];
      
      // Verificar se todos os campos obrigatórios estão presentes
      const existingFieldNames = finalFields.map(field => field.name);
      const missingFields = requiredAnalyticsFields.filter(name => !existingFieldNames.includes(name));
      
      if (missingFields.length > 0) {
        console.warn('⚠️ Campos obrigatórios para analytics não encontrados no template:', missingFields);
        // Adicionar campos obrigatórios faltantes
        missingFields.forEach((fieldName, index) => {
          let defaultField;
          
          // Configuração específica para campo arquivo
          if (fieldName === 'arquivo') {
            defaultField = {
              id: Date.now() + index,
              type: 'file',
              label: 'Anexar evidências (opcional)',
              name: 'arquivo',
              required: false,  // Opcional para o usuário
              placeholder: 'Selecione arquivos para anexar'
            };
          } else {
            defaultField = {
              id: Date.now() + index,
              type: 'radio',
              label: `Campo obrigatório: ${fieldName}`,
              name: fieldName,
              required: true,
              options: ['Opção 1', 'Opção 2']
            };
          }
          finalFields.push(defaultField);
        });
      }

      setFormData(prev => ({
        ...prev,
        // Não sobrescrever title e description - manter os valores que o usuário já digitou
        fields: finalFields
      }));
      toast.success(`Template ${templateKey} carregado com campos obrigatórios para analytics!`);
    }
  };

  // Em que etapa do formulário público cada campo vai cair, pela MESMA regra
  // usada lá (utils/formSteps). Recalcula ao mover campos com ↑ ↓.
  const fieldStepTitles = computeFieldStepTitles(
    Array.isArray(formData.fields) ? formData.fields : []
  );

  // Pergunta que hoje divide a visualização deste cliente (null = sem divisão).
  const routingField = findRoutingField(formData.fields);

  // Submeter formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    // O "Nome do Campo" é a chave sob a qual a resposta é gravada na denúncia.
    // Sem ele, a resposta não tem onde ser guardada e é descartada em silêncio —
    // foi o que aconteceu com 5 formulários que perguntavam quem era o
    // denunciado. Nome repetido causa a mesma perda: a segunda resposta
    // sobrescreve a primeira. Por isso ambos bloqueiam o salvamento.
    const campos = formData.fields || [];

    const semNome = campos
      .map((f, i) => ({ i, f }))
      .filter(({ f }) => !f?.name || String(f.name).trim() === '');
    if (semNome.length > 0) {
      const quais = semNome
        .map(({ i, f }) => `Campo ${i + 1}${f?.label ? ` ("${String(f.label).slice(0, 40)}")` : ''}`)
        .join('; ');
      toast.error(`Preencha o "Nome do Campo" — sem ele a resposta não é gravada. Pendente: ${quais}`);
      return;
    }

    const nomes = campos.map((f) => String(f.name).trim());
    const duplicados = [...new Set(nomes.filter((n, i) => nomes.indexOf(n) !== i))];
    if (duplicados.length > 0) {
      toast.error(`Nome do Campo repetido: ${duplicados.join(', ')}. Cada campo precisa de um nome único, senão uma resposta sobrescreve a outra.`);
      return;
    }

    // Mudou quem vê e quem é avisado? Confirma antes (o modal chama salvar()).
    const impacto = sobreQuemImpact(editingForm ? editingForm.fields : [], campos);
    if (impacto) {
      setImpactoSobreQuem(impacto);
      return;
    }

    await salvar();
  };

  const salvar = async () => {
    try {
      const formattedData = apiUtils.formatFormData(formData);
      
      if (editingForm) {
        await formsApi.updateForm(editingForm.id, formattedData);
        toast.success('Formulário atualizado com sucesso');
      } else {
        await formsApi.createForm(formattedData);
        toast.success('Formulário criado com sucesso');
      }
      
      closeModal();
      fetchForms(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao salvar formulário:', error);
      const errorInfo = apiUtils.handleError(error);
      toast.error(errorInfo.message);
    }
  };

  // Confirmar exclusão
  const handleDelete = async (formId, formTitle) => {
    if (window.confirm(`Tem certeza que deseja deletar o formulário "${formTitle}"?`)) {
      await deleteForm(formId);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl">Carregando formulários...</div>
      </div>
    );
  }

  if (error) {
  return (
      <div className="text-center p-8">
        <div className="text-red-600 text-xl mb-4">Erro ao carregar formulários</div>
        <button
          onClick={fetchForms}
          className="px-4 py-2 bg-blue-500 text-white rounded"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="rf-screen">
      <div className="rf-screen__head">
        <div>
          <h1 className="rf-screen__title">Formulários</h1>
          <p className="rf-screen__sub">
            {(Array.isArray(forms) ? forms : []).length} formulário{(Array.isArray(forms) ? forms : []).length === 1 ? '' : 's'} cadastrado{(Array.isArray(forms) ? forms : []).length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="rf-screen__actions">
          <button onClick={openCreateModal} className="rf-btn rf-btn--primary">
            Novo formulário
          </button>
        </div>
      </div>

      {/* Lista de Formulários */}
      <div style={{ display: 'grid', gap: 12 }}>
        {forms.length === 0 ? (
          <div className="rf-card">
            <div className="rf-empty" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              Nenhum formulário cadastrado ainda.
              <button onClick={openCreateModal} className="rf-btn rf-btn--primary">
                Criar o primeiro
              </button>
            </div>
          </div>
        ) : (
          (Array.isArray(forms) ? forms : []).map((form) => (
            <div key={form.id} className="rf-card" style={{ padding: 20 }}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {form.title}
                  </h3>
                  <p className="text-gray-600 mb-2">{form.description}</p>
                  <div className="flex gap-4 text-sm text-gray-500">
                    <span>Cliente: {form.client_name}</span>
                    <span>Slug: {form.client_slug}</span>
                    <span>Campos: {form.fields?.length || 0}</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleFormStatus(form.id, form.is_active)}
                    className={`px-4 py-2 rounded text-sm font-medium ${
                      form.is_active
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {form.is_active ? 'Ativo' : 'Inativo'}
                  </button>
                  
                  <button
                    onClick={() => openEditModal(form)}
                    className="px-4 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                  >
                    Editar
                  </button>
                  
                  <button
                    onClick={() => handleDelete(form.id, form.title)}
                    className="px-4 py-2 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                  >
                    Deletar
                  </button>
                </div>
              </div>
              
              <div className="text-xs text-gray-400">
                Criado em: {new Date(form.created_at).toLocaleDateString('pt-BR')}
                {form.updated_at && (
                  <span className="ml-4">
                    Atualizado em: {new Date(form.updated_at).toLocaleDateString('pt-BR')}
                  </span>
                )}
            </div>
        </div>
          ))
        )}
      </div>

      {/* Modal de Criação/Edição */}
      {isOpen && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 ${isOpen ? '' : 'hidden'}`}
  style={{ overflowY: 'auto', maxHeight: '90vh' }}
>
  <div className="rf-surface p-6 w-full max-w-3xl modal-content" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingForm ? 'Editar Formulário' : 'Novo Formulário'}
              </h2>
              <button
                  type="button"
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
                  </div>
              
            <form onSubmit={handleSubmit} className="p-6">
                {/* Informações Básicas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                  <label className="block text-sm font-medium mb-2">Título</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded"
                    placeholder="Título do formulário"
                        required
                      />
                    </div>
                
                    <div>
                  <label className="block text-sm font-medium mb-2">Nome do Cliente</label>
                      <input
                        type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded"
                    placeholder="Nome da empresa"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Logo da Empresa</label>
                      
                      {/* Campo de URL */}
                      <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">URL do Logo</label>
                        <input
                          type="url"
                          value={formData.company_logo || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, company_logo: e.target.value }))}
                          className="w-full p-3 border border-gray-300 rounded"
                          placeholder="https://exemplo.com/logo.png"
                        />
                      </div>
                      
                      {/* Upload de arquivo */}
                      <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">OU Upload de Arquivo</label>
                        <div className="flex gap-2">
                          <input
                            type="file"
                            id="logo-upload"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                          <label
                            htmlFor="logo-upload"
                            className="flex-1 p-3 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-400 hover:bg-blue-50 text-center"
                          >
                            <div className="flex flex-col items-center">
                              <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              <span className="text-sm text-gray-600">
                                Clique para fazer upload
                              </span>
                              <span className="text-xs text-gray-400 mt-1">
                                JPG, PNG, SVG, GIF (máx. 5MB)
                              </span>
                            </div>
                          </label>
                        </div>
                        {logoUploading && (
                          <div className="mt-2 text-sm text-blue-600 flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                            Enviando logo...
                          </div>
                        )}
                      </div>
                      
                      {/* Preview do logo */}
                      {formData.company_logo && (
                        <div className="mt-3">
                          <label className="block text-xs text-gray-600 mb-1">Preview do Logo</label>
                          <div className="border border-gray-200 rounded p-4 bg-gray-50 flex items-center justify-center min-h-[80px]">
                            <img
                              src={formData.company_logo}
                              alt="Logo preview"
                              className="max-h-16 max-w-48 object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="hidden flex-col items-center justify-center text-sm text-gray-500">
                              <div className="text-2xl mb-2">❌</div>
                              <div>Erro ao carregar imagem</div>
                              <div className="text-xs mt-1 break-all max-w-48 text-center">
                                {formData.company_logo}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-sm text-gray-500 mt-1">
                        Logo da empresa que aparecerá no formulário público
                      </p>
                    </div>
                
                    <div>
                  <label className="block text-sm font-medium mb-2">Slug do Cliente</label>
                      <input
                        type="text"
                    value={formData.client_slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_slug: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded"
                    placeholder="nome-da-empresa"
                        required
                      />
                    </div>
                
                        <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formData.is_active ? 'true' : 'false'}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                    className="w-full p-3 border border-gray-300 rounded"
                  >
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </select>
                          </div>
                        </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Descrição</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded"
                      rows="3"
                  placeholder="Descrição do formulário"
                    />
                </div>

                {/* Templates */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Templates</label>
                <div className="flex gap-2">
                      <button
                        type="button"
                    onClick={() => loadTemplate('standard')}
                    className="px-4 py-2 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                  >
                    Padrão
                      </button>
                      <button
                        type="button"
                    onClick={() => loadTemplate('vulkan')}
                    className="px-4 py-2 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                  >
                    Vulkan
                      </button>
                          </div>
                        </div>

                {/* Campos do Formulário */}
              <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Campos do Formulário</h3>
                    <button
                      type="button"
                      onClick={addField}
                    className="px-4 py-2 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                    >
                    + Adicionar Campo
                    </button>
                  </div>
                  
                  {/* Como a etapa de cada pergunta é decidida */}
                  <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-indigo-600">📍</span>
                      <span className="text-sm font-medium text-indigo-800">Etapa de cada pergunta</span>
                    </div>
                    <p className="text-xs text-indigo-700">
                      A etiqueta 📍 em cada campo mostra em que etapa do formulário público a pergunta vai
                      aparecer para o denunciante. Ela depende da <strong>posição</strong> do campo: uma
                      pergunta criada por você entra na mesma etapa do campo padrão que vier
                      <strong> acima</strong> dela. Use os botões <strong>↑ ↓</strong> para movê-la de etapa.
                      Perguntas colocadas <strong>depois de todos</strong> os campos padrão aparecem em
                      "Informações Adicionais", no fim.
                    </p>
                  </div>

                  {/* Informação sobre campos obrigatórios */}
                  <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-orange-600">🔒</span>
                      <span className="text-sm font-medium text-orange-800">Campos Obrigatórios para Analytics</span>
                    </div>
                    <p className="text-xs text-orange-700">
                      Os seguintes campos são obrigatórios para garantir dados mínimos para analytics e dashboards: 
                      <strong> local_ocorrencia, tipo_denuncia, quando_ocorreu, departamento, relato_detalhado, tentou_esconder, presenciou_fatos</strong>.
                      Estes campos não podem ser removidos e são automaticamente marcados como obrigatórios.
                    </p>
                  </div>

                  {/* A divisão da visualização é opcional — só para quem quer fluxo distinto */}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-blue-600">🔀</span>
                      <span className="text-sm font-medium text-blue-800">Divisão da visualização (opcional)</span>
                    </div>
                    <p className="text-xs text-blue-700">
                      Marque <strong>"Divide a visualização entre os admins"</strong> numa pergunta de escolha única
                      (ex.: "sobre quem", unidade, filial, local) para cada denúncia ir só para os admins do cliente
                      responsáveis pela opção escolhida, no painel e no e-mail. As opções viram os Níveis de Acesso
                      em Gerenciar Usuários. Uma pergunta por cliente; por padrão é a <strong>sobre_quem</strong>,
                      quando existe. Sem divisão, todos os admins do cliente veem e são avisados de tudo.
                      {' '}Hoje: <strong>{routingField ? (routingField.label || routingField.name) : 'sem divisão'}</strong>.
                      Mudar a pergunta ou as opções pede confirmação ao salvar.
                    </p>
                  </div>

                    {(Array.isArray(formData.fields) ? formData.fields : []).map((field, index) => (
                  <FormField
                    key={field.id || index}
                    field={field}
                    index={index}
                    stepTitle={fieldStepTitles[index]}
                    isRouting={field === routingField}
                    onToggleRouting={toggleRoutingField}
                    onUpdate={updateField}
                    onRemove={removeField}
                    onMoveUp={moveFieldUp}
                    onMoveDown={moveFieldDown}
                    isFirst={index === 0}
                    isLast={index === (formData.fields?.length || 0) - 1}
                  />
                ))}

                {formData.fields.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum campo adicionado. Clique em "Adicionar Campo" para começar.
                          </div>
                        )}
                </div>

              {/* Botões */}
              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                  onClick={closeModal}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingForm ? 'Atualizar' : 'Criar'} Formulário
                  </button>
                </div>
              </form>
          </div>
        </div>
      )}

      {/* Confirmação: a mudança no sobre_quem altera quem vê e quem é avisado */}
      {impactoSobreQuem && (
        <div className="rf-modal" role="dialog" aria-modal="true" aria-labelledby="impacto-sobre-quem-titulo">
          <div className="rf-modal__panel">
            <div className="rf-modal__head">
              <div>
                <h3 id="impacto-sobre-quem-titulo" className="rf-modal__title">
                  {impactoSobreQuem.tipo === 'incluido' && `Dividir a visualização por "${impactoSobreQuem.pergunta}"?`}
                  {impactoSobreQuem.tipo === 'removido' && `Deixar de dividir a visualização por "${impactoSobreQuem.pergunta}"?`}
                  {impactoSobreQuem.tipo === 'trocado' && `Dividir a visualização por "${impactoSobreQuem.pergunta}" em vez de "${impactoSobreQuem.de}"?`}
                  {impactoSobreQuem.tipo === 'categorias' && `Alterar as opções de "${impactoSobreQuem.pergunta}"?`}
                </h3>
                <p className="rf-modal__sub">
                  Isto muda quem vê as denúncias de {formData.client_name || 'este cliente'} e quem recebe o e-mail delas.
                </p>
              </div>
            </div>

            <div className="rf-modal__body text-sm text-gray-700">
              <ul className="list-disc pl-5 space-y-2">
                {impactoSobreQuem.tipo === 'incluido' && (
                  <>
                    <li>
                      Cada denúncia vai para os admins da opção escolhida em "{impactoSobreQuem.pergunta}"
                      {impactoSobreQuem.categorias.length > 0 && <>: <strong>{impactoSobreQuem.categorias.join(', ')}</strong></>}.
                    </li>
                    <li>
                      Admins do cliente com Nível de Acesso atribuído em Gerenciar Usuários só veem e só recebem
                      o e-mail das denúncias do público deles. Quem não tem nível atribuído continua vendo tudo.
                    </li>
                    <li>
                      A denúncia só pode ser aprovada com esse campo preenchido
                      {editingForm && ' — inclusive as que já aguardam aprovação, que vão precisar ser completadas à mão'}.
                    </li>
                  </>
                )}
                {impactoSobreQuem.tipo === 'removido' && (
                  <>
                    <li>
                      Novas denúncias chegam sem público: todos os admins do cliente passam a vê-las e a receber o e-mail
                      {impactoSobreQuem.categorias.length > 0 && <>, inclusive quem hoje cuida só de <strong>{impactoSobreQuem.categorias.join(', ')}</strong></>}.
                    </li>
                    <li>Os Níveis de Acesso deste cliente em Gerenciar Usuários deixam de valer para as novas denúncias.</li>
                    <li>Denúncias já recebidas mantêm o público que tinham.</li>
                  </>
                )}
                {impactoSobreQuem.tipo === 'trocado' && (
                  <>
                    <li>
                      Novas denúncias passam a ir para os admins conforme "{impactoSobreQuem.pergunta}"
                      {impactoSobreQuem.categorias.length > 0 && <>: <strong>{impactoSobreQuem.categorias.join(', ')}</strong></>}.
                      "{impactoSobreQuem.de}" continua no formulário, mas deixa de decidir quem vê.
                    </li>
                    <li>
                      Os Níveis de Acesso dos admins deste cliente precisam ser refeitos em Gerenciar Usuários com
                      as novas opções. Até lá, quem tem nível antigo deixa de ver e de receber as novas denúncias;
                      quem não tem nível atribuído continua vendo tudo.
                    </li>
                    <li>Denúncias já recebidas mantêm o direcionamento que tinham.</li>
                  </>
                )}
                {impactoSobreQuem.tipo === 'categorias' && (
                  <>
                    {impactoSobreQuem.removidas.length > 0 && (
                      <li>
                        Saem: <strong>{impactoSobreQuem.removidas.join(', ')}</strong>. Admins que só têm esse Nível de
                        Acesso deixam de ver e de receber novas denúncias. Revise as permissões em Gerenciar Usuários.
                      </li>
                    )}
                    {impactoSobreQuem.novas.length > 0 && (
                      <li>
                        Entram: <strong>{impactoSobreQuem.novas.join(', ')}</strong>. Até alguém receber esse Nível de
                        Acesso, só os admins sem restrição veem essas denúncias e são avisados delas.
                      </li>
                    )}
                  </>
                )}
              </ul>
            </div>

            <div className="rf-modal__foot">
              <button type="button" className="rf-btn rf-btn--ghost" onClick={() => setImpactoSobreQuem(null)} autoFocus>
                Voltar e revisar
              </button>
              <button
                type="button"
                className="rf-btn rf-btn--primary"
                onClick={async () => {
                  setImpactoSobreQuem(null);
                  await salvar();
                }}
              >
                Confirmar e salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormManagement; 
